const SAFE = [
  /\/shipping(?:-quote|\/quote)(?:\/|$)/i,
  /\/test-connection(?:\/|$)/i,
  /\/signup(?:-info)?(?:\/|$)/i,
  // These authenticated security endpoints manage the CJ confirmation
  // itself. Intercepting confirm-password recursively opened a second modal.
  /\/security\/dropshipping\/(?:confirm-password|revoke-confirmation)(?:\/|$|\?)/i,
];

export const isSensitiveDropshippingRequest = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  const method = String(init.method || (typeof input !== "string" && input?.method) || "GET").toUpperCase();
  const headers = new Headers(init.headers || (typeof input !== "string" ? input?.headers : undefined) || {});
  if (headers.get("X-Elakay-CJ-Confirmation")) return false;
  if (!url.includes("/dropshipping") && !/\/cj\//i.test(url)) return false;
  if (!["POST", "PUT", "PATCH", "DELETE"].includes(method)) return false;
  if (SAFE.some((pattern) => pattern.test(url))) return false;
  return true;
};

export const isMarkedDropshippingProductEdit = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  if (!/\/products\/update\//i.test(url) || String(init.method || "GET").toUpperCase() !== "PATCH") return false;
  try { return JSON.parse(init.body || "{}").dropshippingPasswordRequired === true; } catch (_error) { return false; }
};

// /products/deleteFilter/:productID/:filterID is shared by the generic filter
// UI and CJVariantsSyncPanel's "remove synced variant" button -- only the
// caller knows whether the target product is dropshipping-sourced, so it
// marks the request via header (DELETE has no reliable body convention here)
// rather than us guessing from the URL.
export const isMarkedDropshippingDelete = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  if (!/\/products\/deleteFilter\//i.test(url) || String(init.method || "GET").toUpperCase() !== "DELETE") return false;
  const headers = new Headers(init.headers || (typeof input !== "string" ? input?.headers : undefined) || {});
  return headers.get("X-Dropshipping-Password-Required") === "true";
};

export const isMarkedDropshippingFilterUpdate = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  if (!/\/products\/updateFilter\//i.test(url) || String(init.method || "GET").toUpperCase() !== "PUT") return false;
  const headers = new Headers(init.headers || (typeof input !== "string" ? input?.headers : undefined) || {});
  return headers.get("X-Dropshipping-Password-Required") === "true";
};

export const describeDropshippingRequest = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  const provider = url.match(/\/dropshipping\/(cj|printful|printify|syncee|inventory-source)/i)?.[1]
    || (url.includes("/cj/") ? "cj" : "dropshipping");
  const productId = decodeURIComponent(url.match(/\/products?\/([^/?]+)/i)?.[1] || "");
  const action = url.match(/(sync-with-mapping|convert-variants|sync-keywords|sync-locks|auto-sync|auto-pay|variant-swatch-style|pricing|disconnect|import|sync)/i)?.[1]
    || String(init.method || "modification").toLowerCase();
  let changedFields = [];
  if (typeof init.body === "string") {
    try { changedFields = Object.keys(JSON.parse(init.body)); } catch (_error) { /* no sensitive logging */ }
  }
  return { provider, productId, action, changedFields };
};

export const confirmationActionForRequest = (input, init = {}) => {
  const url = String(typeof input === "string" ? input : input?.url || "");
  const method = String(init.method || (typeof input !== "string" && input?.method) || "GET").toUpperCase();
  if (/\/sync\/all(?:\?|$)/i.test(url)) return "CJ_SYNC_ALL";
  if (/deleteFilter|\/filter\/[^/]+/i.test(url) && method === "DELETE") return "CJ_FILTER_DELETE";
  if (/convert-variants|sync-keywords|\/filter/i.test(url)) return "CJ_FILTER_UPDATE";
  if (method === "DELETE" || /disconnect|remove.*mapping/i.test(url)) return "CJ_SYNC_DELETE";
  return "CJ_SYNC_UPDATE";
};

export const withPasswordConfirmation = (init = {}, confirmationToken, action, metadata = {}) => {
  const next = { ...init, headers: new Headers(init.headers || {}) };
  // dropshippingPasswordRequired is only a local interception marker.  Once
  // confirmation is attached, forward a clean Product patch to the API.
  if (typeof next.body === "string") {
    try {
      const body = JSON.parse(next.body);
      if (body && typeof body === "object" && body.dropshippingPasswordRequired === true) {
        delete body.dropshippingPasswordRequired;
        next.body = JSON.stringify(body);
      }
    } catch (_error) { /* Non-JSON bodies are forwarded unchanged. */ }
  }
  next.headers.set("X-Elakay-CJ-Confirmation", confirmationToken);
  next.headers.set("X-Dropshipping-Action", action);
  next.headers.set("X-Dropshipping-Request-Id", metadata.requestId || "");
  next.headers.set("X-Dropshipping-Synchronization-Id", metadata.synchronizationId || "");
  return next;
};
